import { FC, useContext, useMemo, useState } from "react";
import { Box, createStyles, makeStyles } from "@material-ui/core";
import { throttle } from "lodash";
import moment from "moment";
import { useQueryClient } from "react-query";

import { Image } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { AppContext } from "contexts";
import { useTimer } from "hooks";
import { formatCurrencyAmount, formatDateRange } from "lib/format";
import {
  ACTIVE_OFFER_STATES,
  LIVE_OFFER_STATES,
} from "services/offers/constants";
import { ListingOffer } from "services/offers/types";
import theme from "theme";

import { OfferStatus } from "./OfferStatus";

const useStyles = makeStyles(() =>
  createStyles({
    offerRow: (props: { isActive: boolean }) => ({
      backgroundColor: theme.custom.palette.gray.light,
      opacity: props.isActive ? 1 : 0.5,
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
      textAlign: "left",
      margin: theme.spacing(1, 0),
      boxShadow: theme.shadows[3],
      borderRadius: theme.spacing(0.375),
      width: "100%",
      "& > div:first-of-type": {
        display: "flex",
        width: 35,
        borderRight: "1px solid #e1e1e3",
      },
      "& > div:last-child": {
        width: "100%",
        padding: theme.spacing(1.25),
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      },
      "& > div:last-child > div:first-of-type > div:first-of-type": {
        fontSize: 21,
        fontWeight: theme.typography.fontWeightMedium,
        margin: theme.spacing(0.625, 0),
      },
      "& > div:last-child > div:first-of-type > div:last-child": {
        fontSize: 16,
        marginTop: theme.spacing(0.25),
        marginBottom: 0,
        [theme.breakpoints.down("xs")]: {
          fontSize: 14,
        },
      },
    }),
    offerUserImage: {
      margin: "auto",
      width: "60%",
    },
    counterofferLinkImage: (props: {
      offerIndex: number;
      isActive: boolean;
    }) => ({
      position: "relative",
      top: props.offerIndex === 1 ? 14 : 25,
      opacity: props.isActive ? 1 : 0.5,
      margin: theme.spacing(0, 1),
    }),
  }),
);

interface OfferRowProps {
  setIsAcceptOfferModalOpen: () => void;
  setIsRejectOfferModalOpen: () => void;
  setIsCounterofferModalOpen: () => void;
  setIsEditOfferModalOpen: () => void;
  setIsWithdrawOfferModalOpen: () => void;
  listingId: number;
  offer: ListingOffer;
  offerIndex: number;
  isActive: boolean;
  isAssetOwner: boolean;
}

const OfferRow: FC<OfferRowProps> = ({
  setIsAcceptOfferModalOpen,
  setIsRejectOfferModalOpen,
  setIsCounterofferModalOpen,
  setIsEditOfferModalOpen,
  setIsWithdrawOfferModalOpen,
  listingId,
  offer,
  offerIndex,
  isActive,
  isAssetOwner,
}) => {
  const queryClient = useQueryClient();
  const { listing } = useAssetDetailData(listingId);
  const { user } = useContext(AppContext);

  const offerRowActive = isActive || ACTIVE_OFFER_STATES.includes(offer.state);
  const classes = useStyles({
    isActive: offerRowActive,
    offerIndex,
  });

  const currentTime = useTimer();

  const isCounteroffer = !!offer.countered_offer_id;

  // below we are handling the case that an offer's expiration is in the past
  // but the offer's state has not been updated by the daemon yet
  // 1) set `isLiveOffer` to false to hide offer action buttons
  // 2) use `throttledRefetch` so `refetchQueries` is only invoked every 5
  //    seconds: the daemon should run every 5 seconds
  // 3) set a timeout to cancel the throttled refetch after 15 seconds
  let isLiveOffer = LIVE_OFFER_STATES.includes(offer.state);
  const [isCancelSet, setIsCancelSet] = useState(false);
  const throttledRefetch = useMemo(
    () =>
      throttle(
        () => {
          queryClient.refetchQueries(["listing", listing.id.toString()]);
        },
        5000,
        { leading: true },
      ),
    [listing.id, queryClient],
  );
  if (isLiveOffer && moment(currentTime).diff(offer.expiration, "s") >= 0) {
    isLiveOffer = false;
    throttledRefetch();
    if (!isCancelSet) {
      setIsCancelSet(true);
      setTimeout(() => {
        throttledRefetch.cancel();
      }, 15000);
    }
  }

  return (
    <Box display="flex">
      {isCounteroffer && (
        <Image
          name={
            offerIndex === 1
              ? "counteroffer-link-short.svg"
              : "counteroffer-link-long.svg"
          }
          className={classes.counterofferLinkImage}
        />
      )}
      <Box className={classes.offerRow}>
        <div>
          {offer.kind === "seller_counteroffer" ? (
            <Image
              name="counteroffer-seller-icon.svg"
              className={classes.offerUserImage}
            />
          ) : offer.kind === "buyer_counteroffer" ? (
            <Image
              name="counteroffer-buyer-icon.svg"
              className={classes.offerUserImage}
            />
          ) : offer.countered_offer_id ? (
            <Image
              name="counteroffer-made-icon.svg"
              className={classes.offerUserImage}
            />
          ) : offer.buyer_id === user?.userId ? (
            <Image
              name="orderbook-user.svg"
              className={classes.offerUserImage}
            />
          ) : null}
        </div>
        <div>
          <Box display="flex" flexDirection="column" alignItems="space-between">
            <Box style={{ margin: "0px" }}>
              {formatCurrencyAmount("USD", offer.amount)}
            </Box>
            {!isCounteroffer && (
              <div>
                {offer.buyer_id === user?.userId
                  ? "Your bid was made "
                  : `Buyer ${offer.bidder_index} made `}
                {formatDateRange(currentTime, offer.created_at)} ago
                {offer.kind === "automatic_offer" ? " via Standing Order" : ""}
              </div>
            )}
          </Box>
          <OfferStatus
            listing={listing}
            offer={offer}
            isLiveOffer={isLiveOffer}
            isAssetOwner={isAssetOwner}
            isChainActive={isActive}
            setIsAcceptOfferModalOpen={setIsAcceptOfferModalOpen}
            setIsRejectOfferModalOpen={setIsRejectOfferModalOpen}
            setIsCounterofferModalOpen={setIsCounterofferModalOpen}
            setIsEditOfferModalOpen={setIsEditOfferModalOpen}
            setIsWithdrawOfferModalOpen={setIsWithdrawOfferModalOpen}
          />
        </div>
      </Box>
    </Box>
  );
};

export default OfferRow;
